import { useState, useEffect } from "react";
import {
  AppShell, PageHeader, ModuleHeader, SearchBar, GlobalSearch, NotificationCenter,
  StatsGrid, FilterBar, EntityList, EntityTable, EntityDrawer, EntityTimeline,
  TaskCard, ManualCard, IncidentCard, AIAnalysisCard, Checklist,
  AttachmentGallery, CommentThread, AuditTimeline, ActivityFeed, EmptyState, LoadingState,
} from "@/components/library";
import {
  mockTasks, mockManual, mockIncident, mockChecklist, mockComments, mockAttachments,
  mockAudit, mockAI, mockNotifications, mockKpis, mockNav, mockSearchGroups, mockUsers,
} from "@/lib/mock";

function Section({ id, title, subtitle, children }) {
  return (
    <section id={id} className="scroll-mt-20">
      <div className="mb-5">
        <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      <div className="rounded-2xl border border-border bg-card/40 p-5 sm:p-6">{children}</div>
    </section>
  );
}

const DEPARTMENT = { name: "Kök", icon: "ChefHat", color: "#E15B2D", description: "Mise en place, matsäkerhet och köksrutiner." };
const TABS = [
  { key: "oversikt", label: "Översikt", icon: "LayoutGrid" },
  { key: "checklists", label: "Checklistor", icon: "ClipboardList" },
  { key: "tasks", label: "Uppgifter", icon: "CheckSquare" },
  { key: "manuals", label: "Manualer", icon: "BookOpen" },
  { key: "incidents", label: "Problem", icon: "AlertTriangle" },
  { key: "ai", label: "AI", icon: "Bot" },
];

export default function Library() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("oversikt");
  const [filters, setFilters] = useState({ status: "todo" });
  const [drawer, setDrawer] = useState(null);
  const [notes, setNotes] = useState(mockNotifications);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const toggleFilter = (g, v) => setFilters((f) => ({ ...f, [g]: f[g] === v ? undefined : v }));
  const markRead = (id) => setNotes((n) => n.map((x) => (x.id === id ? { ...x, read: true } : x)));

  const filterChips = [
    { group: "status", value: "todo", label: "Att göra" },
    { group: "status", value: "in_progress", label: "Pågår" },
    { group: "status", value: "done", label: "Klar" },
  ];

  return (
    <AppShell
      brand="Restaurangkollen"
      navItems={mockNav}
      activeKey="kok"
      user={{ name: "Sara Berg", role: "chef", title: "Restaurangchef" }}
      topbarRight={
        <div className="flex items-center gap-2">
          <SearchBar onClick={() => setSearchOpen(true)} />
          <NotificationCenter notifications={notes} onRead={markRead} />
        </div>
      }
    >
      <div className="max-w-[1100px] mx-auto p-6 sm:p-10 space-y-12">
        <PageHeader
          overline="Steg 3 · Komponentbibliotek"
          title="Komponentgalleri"
          description="Återanvändbara domänkomponenter, metadata-drivna via entity-registry. Varje skärm i systemet monteras av dessa — ingen affärslogik, ingen duplicerad UI."
        />

        <Section title="ModuleHeader" subtitle="Avdelningshuvud + funktionella modulflikar (regel 7).">
          <ModuleHeader department={DEPARTMENT} tabs={TABS} active={tab} onTab={setTab} />
        </Section>

        <Section title="StatsGrid / KPIWidget" subtitle="Nyckeltal med trend.">
          <StatsGrid items={mockKpis} />
        </Section>

        <Section title="FilterBar" subtitle="Konfigurerbara filterchips.">
          <FilterBar filters={filterChips} active={filters} onToggle={toggleFilter} />
        </Section>

        <Section title="EntityList → EntityCard" subtitle="Generiska, registry-drivna kort. Klicka för EntityDrawer.">
          <EntityList
            entities={mockTasks.map((t) => ({ ...t, has_ai: true }))}
            onOpen={(e) => setDrawer(e)} columns={3}
          />
        </Section>

        <Section title="EntityTable" subtitle="Tät, responsiv tabellvy av samma data.">
          <EntityTable entityType="task" rows={mockTasks} onOpen={(e) => setDrawer(e)} />
        </Section>

        <Section title="Specialiserade kort" subtitle="TaskCard · ManualCard · IncidentCard.">
          <div className="grid sm:grid-cols-3 gap-4">
            <TaskCard task={mockTasks[0]} onOpen={() => setDrawer({ ...mockTasks[0], has_ai: true })} />
            <ManualCard manual={mockManual} onOpen={() => setDrawer(mockManual)} />
            <IncidentCard incident={mockIncident} onOpen={() => setDrawer(mockIncident)} />
          </div>
        </Section>

        <Section title="Checklist + ChecklistItem" subtitle="Foto-/AI-krav per moment, progress.">
          <Checklist checklist={mockChecklist} />
        </Section>

        <Section title="AIAnalysisCard" subtitle="AI-analys med modell, prompt-version, säkerhet och åtgärder.">
          <AIAnalysisCard analysis={mockAI} />
        </Section>

        <div className="grid lg:grid-cols-2 gap-6">
          <Section title="CommentThread" subtitle="Kommentarer + komponering.">
            <CommentThread comments={mockComments} canComment onSubmit={() => {}} />
          </Section>
          <Section title="AttachmentGallery" subtitle="Bilagor med AI-taggar.">
            <AttachmentGallery attachments={mockAttachments} canAdd onAdd={() => {}} />
          </Section>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Section title="AuditTimeline" subtitle="Oföränderlig ändringshistorik med diff.">
            <AuditTimeline events={mockAudit} />
          </Section>
          <Section title="ActivityFeed" subtitle="Senaste händelser.">
            <ActivityFeed items={mockAudit.map((a) => ({ ...a }))} />
          </Section>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Section title="EntityTimeline" subtitle="Livscykel för en entitet.">
            <EntityTimeline items={[
              { label: "Problem rapporterat", actor: "Marcus Holm", at: "2026-06-27T11:00:00Z" },
              { label: "Status → Utreds", actor: "Sara Berg", at: "2026-06-27T11:35:00Z" },
              { label: "AI-analys genererad", actor: "System", at: "2026-06-27T11:36:00Z" },
            ]} />
          </Section>
          <Section title="EmptyState / LoadingState" subtitle="Tomt- och laddningstillstånd.">
            <div className="space-y-4">
              <LoadingState rows={2} />
              <EmptyState title="Inget att visa" hint="När data finns dyker den upp här." />
            </div>
          </Section>
        </div>

        <Section title="GlobalSearch (⌘K)" subtitle="Ett index för alla entiteter — används även av AI. Tryck ⌘K eller knappen.">
          <button onClick={() => setSearchOpen(true)} data-testid="open-global-search"
            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 active:scale-95 transition-all">
            Öppna global sök
          </button>
        </Section>
      </div>

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} query={query} onQuery={setQuery}
        groups={mockSearchGroups} onSelect={() => setSearchOpen(false)} />

      <EntityDrawer
        open={!!drawer} entity={drawer} role="chef" onClose={() => setDrawer(null)} onDelete={() => setDrawer(null)}
        detail={
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">{drawer?.description || drawer?.content || "Ingen beskrivning."}</p>
            {drawer?.assignee && <p><span className="text-muted-foreground">Ansvarig:</span> {drawer.assignee.name}</p>}
          </div>
        }
        comments={<CommentThread comments={mockComments} canComment onSubmit={() => {}} />}
        attachments={<AttachmentGallery attachments={mockAttachments} canAdd onAdd={() => {}} />}
        ai={<AIAnalysisCard analysis={mockAI} />}
        audit={<AuditTimeline events={mockAudit} />}
      />
    </AppShell>
  );
}
